import { useEffect, useRef, type RefObject } from 'react';
import type { HeartbeatRequest, HeartbeatResponse } from '@lp/shared';
import { api, getAccessToken } from './api.js';

const BEAT_INTERVAL_MS = 12_000;
/** The server's own ceiling on `deltaSec`; anything larger is a bug, not a beat. */
const MAX_DELTA_SEC = 120;
const SEEK_SETTLE_MS = 600;

/**
 * The watch heartbeat.
 *
 * Two rules shape this, and both are about not lying to the progress table:
 *
 *  1. It beats **only while the video is actually playing and the tab is
 *     visible**. A paused player parked on a desk for an hour, or a
 *     background tab, must not accrue watch time — so the interval is started
 *     by `playing` and torn down by `pause`, `ended` and `visibilitychange`.
 *  2. `deltaSec` is wall-clock time spent playing since the previous beat,
 *     accumulated from the moments playback was actually running, not
 *     `BEAT_INTERVAL_MS` assumed. `positionSec` is where the playhead is now,
 *     which is a different number after a seek.
 *
 * One-shot beats fire on pause, once a seek settles, and on
 * `visibilitychange`/`pagehide` so the last few seconds before a tab closes
 * are not lost. The unload beat goes out as a `keepalive` fetch — a normal
 * fetch is cancelled with the document, and `navigator.sendBeacon` cannot
 * carry the Authorization header this API requires.
 */
export function useProgressHeartbeat(options: {
  sessionId: string;
  videoRef: RefObject<HTMLVideoElement | null>;
  /** False while the player has nothing to play (no video, still transcoding). */
  enabled: boolean;
  durationSec: number | null;
  /** Called once, on the beat that flipped the session to complete. */
  onJustCompleted: () => void;
}): void {
  const { sessionId, videoRef, enabled, durationSec, onJustCompleted } = options;

  const accumulatedRef = useRef(0);
  const playingSinceRef = useRef<number | null>(null);
  const intervalRef = useRef<number | null>(null);
  const seekTimerRef = useRef<number | null>(null);
  const celebratedRef = useRef(false);

  const completedCallback = useRef(onJustCompleted);
  completedCallback.current = onJustCompleted;

  const durationRef = useRef(durationSec);
  durationRef.current = durationSec;

  useEffect(() => {
    const video = videoRef.current;
    if (!enabled || !video || !sessionId) return;

    /** Fold the currently-running play stretch into the pending delta. */
    const fold = (): void => {
      if (playingSinceRef.current !== null) {
        accumulatedRef.current += (Date.now() - playingSinceRef.current) / 1000;
        playingSinceRef.current = null;
      }
    };

    const takeDelta = (): number => {
      const delta = Math.min(Math.max(accumulatedRef.current, 0), MAX_DELTA_SEC);
      accumulatedRef.current = 0;
      return Number(delta.toFixed(2));
    };

    const payload = (deltaSec: number): HeartbeatRequest => ({
      positionSec: Number.isFinite(video.currentTime) ? Math.max(0, video.currentTime) : 0,
      deltaSec,
      durationSec:
        durationRef.current ??
        (Number.isFinite(video.duration) && video.duration > 0 ? video.duration : null),
    });

    const beat = (): void => {
      fold();
      const delta = takeDelta();
      // Restart the stretch immediately so a beat mid-playback does not drop
      // the seconds between the fold and the next event.
      if (!video.paused && !video.ended) playingSinceRef.current = Date.now();

      void api
        .post<HeartbeatResponse>(`/sessions/${encodeURIComponent(sessionId)}/progress`, payload(delta))
        .then((res) => {
          if (res.justCompleted && !celebratedRef.current) {
            celebratedRef.current = true;
            completedCallback.current();
          }
        })
        .catch(() => {
          // A dropped beat is not worth interrupting playback over; the next
          // one carries the position forward and the delta is already spent.
        });
    };

    /** The last beat of a page's life: survives the document going away. */
    const finalBeat = (): void => {
      fold();
      const delta = takeDelta();
      if (delta <= 0) return;
      const token = getAccessToken();
      try {
        void fetch(`/api/sessions/${encodeURIComponent(sessionId)}/progress`, {
          method: 'POST',
          keepalive: true,
          credentials: 'include',
          headers: {
            'content-type': 'application/json',
            ...(token ? { authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(payload(delta)),
        });
      } catch {
        /* the tab is going away; there is nothing left to tell the user */
      }
    };

    const stopInterval = (): void => {
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const startInterval = (): void => {
      if (intervalRef.current !== null) return;
      intervalRef.current = window.setInterval(beat, BEAT_INTERVAL_MS);
    };

    const onPlaying = (): void => {
      if (document.visibilityState !== 'visible') return;
      playingSinceRef.current = Date.now();
      startInterval();
    };

    const onPause = (): void => {
      stopInterval();
      beat();
      playingSinceRef.current = null;
    };

    const onEnded = (): void => {
      stopInterval();
      beat();
      playingSinceRef.current = null;
    };

    const onSeeked = (): void => {
      if (seekTimerRef.current !== null) window.clearTimeout(seekTimerRef.current);
      seekTimerRef.current = window.setTimeout(() => {
        seekTimerRef.current = null;
        beat();
      }, SEEK_SETTLE_MS);
    };

    const onVisibility = (): void => {
      if (document.visibilityState === 'hidden') {
        stopInterval();
        fold();
        if (accumulatedRef.current > 0) finalBeat();
        playingSinceRef.current = null;
      } else if (!video.paused && !video.ended) {
        playingSinceRef.current = Date.now();
        startInterval();
      }
    };

    video.addEventListener('playing', onPlaying);
    video.addEventListener('pause', onPause);
    video.addEventListener('ended', onEnded);
    video.addEventListener('seeked', onSeeked);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', finalBeat);

    if (!video.paused && !video.ended) onPlaying();

    return () => {
      video.removeEventListener('playing', onPlaying);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('ended', onEnded);
      video.removeEventListener('seeked', onSeeked);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', finalBeat);
      stopInterval();
      if (seekTimerRef.current !== null) window.clearTimeout(seekTimerRef.current);
      // Leaving the page is the same event as closing the tab, as far as the
      // seconds already watched are concerned.
      finalBeat();
    };
  }, [enabled, sessionId, videoRef]);
}
